import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import FormField from '@/components/shared/FormField'

/**
 * Password input with visually-hidden label, show/hide toggle, and correct
 * autocomplete wiring. Drop-in replacement for the inline password fields in
 * Login and ResetPassword.
 *
 * The show/hide toggle state is local — sibling password fields get their
 * own independent toggles (intentional: the "new password" and "confirm
 * new password" fields shouldn't reveal together, so a user can catch typos).
 *
 * Caller controls the visual style via `className` because Login and
 * ResetPassword use slightly different card treatments (rounded-xl vs
 * rounded-lg, bg-surface vs bg-surface-high). Keeping styling as a prop
 * avoids a drive-by visual change here.
 *
 * A11Y-009 wiring (2026-05-06): the label, error message, and
 * aria-describedby plumbing are now owned by FormField. Existing callers
 * that don't pass `error` get the same visual + behavior as before; new
 * callers gain per-field error announcements by setting it.
 */
export default function PasswordField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  className,
  error,
}) {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <FormField label={label} srLabel error={error}>
      {props => (
        <div className="relative">
          <input
            {...props}
            type={showPassword ? 'text' : 'password'}
            autoComplete={autoComplete}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={className}
          />
          <button
            type="button"
            onClick={() => setShowPassword(s => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors"
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" aria-hidden="true" />
            ) : (
              <Eye className="w-5 h-5" aria-hidden="true" />
            )}
          </button>
        </div>
      )}
    </FormField>
  )
}
